import React, { FC, useMemo, useState } from "react";
import { Progress, Avatar, Input, Empty } from "antd";
import { UserOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  RsvpChoice,
  RsvpRespondent,
} from "./types";
import "./MessageTypes.scss";

/**
 * RSVP message type — analytics on top, roster underneath.
 *
 * There is no charting library in this project, so the rings are antd's
 * circular Progress. That is enough: each ring is a single percentage, which is
 * exactly what Progress renders.
 */

interface RsvpCardProps {
  eventTitle: string;
  invited: number;
  respondents: RsvpRespondent[];
}

const CHOICES: {
  key: RsvpChoice | "all";
  label: string;
  color: string;
}[] = [
  { key: "yes", label: "Yes", color: "#40CC52" },
  { key: "maybe", label: "Maybe", color: "#FBAE16" },
  { key: "no", label: "No", color: "#FF5F25" },
];

const RsvpCard: FC<RsvpCardProps> = ({ eventTitle, invited, respondents }) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<RsvpChoice | "all">("all");

  const counts = useMemo(
    () => ({
      yes: respondents.filter((r) => r.choice === "yes").length,
      maybe: respondents.filter((r) => r.choice === "maybe").length,
      no: respondents.filter((r) => r.choice === "no").length,
    }),
    [respondents]
  );

  const responseRate = invited
    ? Math.round((respondents.length / invited) * 100)
    : 0;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return respondents.filter((person) => {
      if (filter !== "all" && person.choice !== filter) return false;
      if (!needle) return true;
      return person.name.toLowerCase().includes(needle);
    });
  }, [respondents, query, filter]);

  const pct = (value: number) =>
    respondents.length ? Math.round((value / respondents.length) * 100) : 0;

  return (
    <section className="mt-card mt-rsvp">
      <header className="mt-card__head">
        <h4 title={eventTitle}>{eventTitle}</h4>
      </header>

      {/* ---- analytics ---- */}
      <div className="mt-rsvp__stats">
        {CHOICES.map((choice) => {
          const value = counts[choice.key as RsvpChoice];
          return (
            <button
              key={choice.key}
              type="button"
              className={`mt-rsvp__ring ${
                filter === choice.key ? "is-active" : ""
              }`}
              onClick={() =>
                setFilter(filter === choice.key ? "all" : (choice.key as RsvpChoice))
              }
            >
              <Progress
                type="circle"
                width={68}
                strokeWidth={9}
                strokeColor={choice.color}
                trailColor="#eef1f5"
                percent={pct(value)}
                format={() => <span className="mt-rsvp__ring-value">{value}</span>}
              />
              <span className="mt-rsvp__ring-label">{choice.label}</span>
            </button>
          );
        })}

        <div className="mt-rsvp__rate">
          <span className="mt-rsvp__rate-value">{responseRate}%</span>
          <span className="mt-rsvp__rate-label">Response rate</span>
          <span className="mt-rsvp__rate-sub">
            {respondents.length} of {invited} invited
          </span>
        </div>
      </div>

      {/* ---- roster ---- */}
      <div className="mt-rsvp__roster">
        <Input
          allowClear
          className="mt-rsvp__search"
          prefix={<SearchOutlined />}
          placeholder="Search respondents"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {filter !== "all" && (
          <button
            type="button"
            className="mt-rsvp__clear"
            onClick={() => setFilter("all")}
          >
            Showing “{filter}” only — clear
          </button>
        )}

        <ul className="mt-rsvp__list">
          {visible.map((person) => (
            <li key={person.id} className="mt-rsvp__person">
              <Avatar size={30} src={person.avatar} icon={<UserOutlined />} />
              <div className="mt-rsvp__person-meta">
                <b>{person.name}</b>
                <em>
                  {moment(person.respondedAt).format("D MMM, h:mm A")}
                  {person.note ? ` · ${person.note}` : ""}
                </em>
              </div>
              <span className={`mt-pill mt-pill--rsvp-${person.choice}`}>
                {person.choice}
              </span>
            </li>
          ))}
        </ul>

        {visible.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No matching respondents"
          />
        )}
      </div>
    </section>
  );
};

export default RsvpCard;
